import { useState } from 'react'
import { getReports, exportReportsCSV } from '../api'
import ProcessingOverlay from '../components/ui/ProcessingOverlay'

const fmt = (n: number) => Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const PAYMENT_LABEL: Record<string, string> = {
  CASH: 'Cash', BANK_TRANSFER: 'Transfer', CREDIT: 'Credit'
}

export default function Reports() {
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const firstOfMonth = today.slice(0, 8) + '01'

  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)
  const [customerFilter, setCustomerFilter] = useState('')
  const [invoiceFilter, setInvoiceFilter] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [processing, setProcessing] = useState('')

  const load = async () => {
    setLoading(true)
    const params: any = { date_from: dateFrom, date_to: dateTo }
    if (customerFilter.trim()) params.customer = customerFilter.trim()
    if (invoiceFilter.trim()) params.invoice_number = invoiceFilter.trim()
    if (departmentFilter) params.department = departmentFilter
    const r: any = await getReports(params)
    setData(r.data)
    setLoading(false)
    setSearched(true)
  }

  const handleExport = async () => {
    setProcessing('Exporting CSV...')
    try {
      const params: any = { date_from: dateFrom, date_to: dateTo }
      if (customerFilter.trim()) params.customer = customerFilter.trim()
      if (invoiceFilter.trim()) params.invoice_number = invoiceFilter.trim()
      if (departmentFilter) params.department = departmentFilter
      const r: any = await exportReportsCSV(params)
      const url = window.URL.createObjectURL(new Blob([r.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `report_${dateFrom}_${dateTo}.csv`
      a.click()
    } finally { setProcessing('') }
  }

  const grandTotal = data.reduce((s, r) => s + r.grand_total, 0)
  const totalDiscount = data.reduce((s, r) => s + r.discount_total, 0)

  return (
    <div className="page">
      {processing && <ProcessingOverlay message={processing} />}
      <div className="page-header">
        <div>
          <div className="page-title">Reports</div>
          <div className="page-subtitle">Invoice report by date range</div>
        </div>
        {data.length > 0 && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={handleExport}>⬇ Export CSV</button>
            <button className="btn btn-ghost" onClick={() => window.print()}>🖨 Print</button>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">From Date</label>
            <input type="date" className="form-input" style={{ width: 160 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">To Date</label>
            <input type="date" className="form-input" style={{ width: 160 }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Customer</label>
            <input className="form-input" style={{ width: 200 }} placeholder="Filter by customer..." value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Department</label>
            <select className="form-input" style={{ width: 160 }} value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}>
              <option value="">All</option>
              <option value="SIL">Laptop (SIL)</option>
              <option value="SIH">Mobile (SIH)</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Invoice Number</label>
            <input className="form-input" style={{ width: 200 }} placeholder="Search invoice..." value={invoiceFilter} onChange={e => setInvoiceFilter(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {searched && (
        <div className="card">
          {data.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No invoices found for this period</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 20px', flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>TOTAL INVOICES</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{data.length}</div>
                </div>
                <div style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 20px', flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>TOTAL REVENUE</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#2563eb' }}>{fmt(grandTotal)}</div>
                </div>
                <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 20px', flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>TOTAL DISCOUNT</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#ef4444' }}>{fmt(totalDiscount)}</div>
                </div>
              </div>

              <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Payment</th>
                    <th className="right">Discount</th>
                    <th className="right">Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r: any, i: number) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600, color: '#2563eb' }}>{r.invoice_number}</td>
                      <td style={{ color: '#64748b' }}>{r.invoice_date.split('-').reverse().join('-')}</td>
                      <td>{r.customer_name}</td>
                      <td>{PAYMENT_LABEL[r.payment_type] || r.payment_type}</td>
                      <td className="right" style={{ color: '#ef4444' }}>{r.discount_total > 0 ? fmt(r.discount_total) : '-'}</td>
                      <td className="right" style={{ fontWeight: 700 }}>{fmt(r.grand_total)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                    <td colSpan={4} style={{ textAlign: 'right', paddingRight: 8 }}>TOTAL</td>
                    <td className="right" style={{ color: '#ef4444' }}>{fmt(totalDiscount)}</td>
                    <td className="right" style={{ color: '#2563eb' }}>{fmt(grandTotal)}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
